import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useAccountingMode } from '../contexts/AccountingModeContext';
import { Card } from '../components/ui/card';
import StockAlertBadge from '../components/StockAlertBadge';
import { DollarSign, TrendingUp, AlertTriangle, Package, Factory, Truck, Clock, Users, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { getAuthHeaders } = useAuth();
  const { mode } = useAccountingMode();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [payrollWeek, setPayrollWeek] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchPendingDeliveries();
    fetchPayrollWeek();
  }, [mode]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats?accounting_mode=${mode}`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/pending-deliveries`, getAuthHeaders());
      setPendingDeliveries(response.data?.pending || []);
    } catch (error) {
      console.error('Error fetching pending deliveries:', error);
    }
  };

  const fetchPayrollWeek = async () => {
    try {
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const start = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
      const response = await axios.get(`${API}/payroll/week?start=${start}`, getAuthHeaders());
      setPayrollWeek(response.data);
    } catch (error) {
      console.error('Error fetching payroll week:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  const kpis = [
    {
      label: 'Chiffre d\'Affaires',
      value: `${stats?.total_revenue?.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} FCFA`,
      icon: DollarSign,
      color: 'bg-green-100 text-green-700',
      testId: 'kpi-revenue'
    },
    {
      label: 'Dépenses Totales',
      value: `${stats?.total_expenses?.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} FCFA`,
      icon: TrendingUp,
      color: 'bg-red-100 text-red-700',
      testId: 'kpi-expenses'
    },
    {
      label: 'Bénéfice',
      value: `${stats?.profit?.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} FCFA`,
      icon: DollarSign,
      color: stats?.profit >= 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700',
      testId: 'kpi-profit'
    },
    {
      label: 'Alertes Stock',
      value: stats?.low_stock_alerts || 0,
      icon: AlertTriangle,
      color: 'bg-yellow-100 text-yellow-700',
      testId: 'kpi-alerts'
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Tableau de Bord
        </h1>
        <p className="text-sm text-slate-600 mt-1">Vue d'ensemble de votre activité</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="bg-white border border-slate-200 rounded-sm shadow-sm p-6" data-testid={kpi.testId}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-600 uppercase tracking-wider">{kpi.label}</p>
                  <p className="text-2xl font-bold text-[#1B3268] mt-2">{kpi.value}</p>
                </div>
                <div className={`${kpi.color} p-3 rounded-sm`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Stock Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Raw Materials */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="raw-materials-card">
          <div className="border-b border-slate-100 p-4 bg-slate-50/50">
            <h3 className="text-xl font-semibold text-[#1B3268] flex items-center gap-2">
              <Package className="w-5 h-5" />
              Matières Premières
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {stats?.raw_materials?.map((material) => (
                <div key={material.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm" data-testid={`material-${material.type}`}>
                  <div>
                    <p className="font-medium text-slate-900">{material.name}</p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Stock: {Number(material.current_stock).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {material.unit} | Seuil: {Number(material.alert_threshold).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {material.unit}
                    </p>
                  </div>
                  <StockAlertBadge current={material.current_stock} threshold={material.alert_threshold} />
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Finished Products */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="finished-products-card">
          <div className="border-b border-slate-100 p-4 bg-slate-50/50">
            <h3 className="text-xl font-semibold text-[#1B3268] flex items-center gap-2">
              <Factory className="w-5 h-5" />
              Produits Finis
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {stats?.finished_products?.map((product) => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm" data-testid={`product-${product.type}`}>
                  <div>
                    <p className="font-medium text-slate-900">{product.name}</p>
                    <p className="text-xs text-slate-600 mt-0.5">Stock: {product.current_stock} {product.unit}</p>
                  </div>
                  <span className="text-2xl font-bold text-[#1B3268]">{product.current_stock}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Masse salariale — semaine en cours */}
      {payrollWeek && (() => {
        const rows = payrollWeek.rows || [];
        const gross = rows.reduce((s, r) => s + (r.payment ? r.payment.base_salary : r.gross), 0);
        const paid = rows.filter((r) => r.payment).reduce((s, r) => s + r.payment.net_salary, 0);
        const toPay = rows.filter((r) => !r.payment).reduce((s, r) => s + Math.max(0, r.gross - r.suggested_deduction), 0);
        const pointed = rows.filter((r) => r.days_worked > 0).length;
        const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
        return (
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="payroll-week-card">
            <div className="border-b border-slate-100 p-4 bg-slate-50/50 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#1B3268] flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Masse salariale — semaine en cours
              </h3>
              <Link to="/rh" className="text-xs font-medium text-blue-600 hover:underline" data-testid="payroll-week-link">
                Gérer la paie →
              </Link>
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1"><Users className="w-3.5 h-3.5" />Employés pointés</p>
                <p className="text-2xl font-bold text-[#1B3268] font-mono mt-1" data-testid="payroll-week-pointed">{pointed}<span className="text-sm text-slate-400 font-normal"> / {rows.length}</span></p>
              </div>
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Brut semaine</p>
                <p className="text-2xl font-bold text-[#1B3268] font-mono mt-1" data-testid="payroll-week-gross">{fmt(gross)} F</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-sm">
                <p className="text-xs text-emerald-700 uppercase tracking-wider">Déjà payé</p>
                <p className="text-2xl font-bold text-emerald-700 font-mono mt-1" data-testid="payroll-week-paid">{fmt(paid)} F</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-sm">
                <p className="text-xs text-orange-700 uppercase tracking-wider">Reste à payer</p>
                <p className="text-2xl font-bold text-orange-700 font-mono mt-1" data-testid="payroll-week-topay">{fmt(toPay)} F</p>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Livraisons en cours (devis validés avec livraisons partielles) */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="pending-deliveries-card">
        <div className="border-b border-slate-100 p-4 bg-blue-50/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" />
            <h3 className="text-xl font-semibold text-[#1B3268]">Livraisons en cours</h3>
            {pendingDeliveries.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-bold bg-blue-600 text-white rounded-full" data-testid="pending-deliveries-count">
                {pendingDeliveries.length}
              </span>
            )}
          </div>
          <span className="text-xs text-slate-500">Trié du plus ancien au plus récent</span>
        </div>
        <div className="p-6">
          {pendingDeliveries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                    <th className="p-3 text-left">Référence</th>
                    <th className="p-3 text-left">Client / Chantier</th>
                    <th className="p-3 text-center">Validé depuis</th>
                    <th className="p-3 text-center">Livraisons</th>
                    <th className="p-3 text-right">Progression</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingDeliveries.map((d) => {
                    const isOld = (d.days_since_validation || 0) >= 7;
                    return (
                      <tr key={d.id} className="hover:bg-blue-50 transition-colors border-b last:border-0" data-testid={`pending-devis-${d.reference}`}>
                        <td className="p-3 font-mono font-semibold text-[#1B3268]">{d.reference}</td>
                        <td className="p-3">
                          <div className="font-medium">{d.client_name}</div>
                          {d.chantier && <div className="text-xs text-slate-500">{d.chantier}</div>}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isOld ? 'text-red-600' : 'text-slate-700'}`}>
                            <Clock className="w-3 h-3" />
                            {d.days_since_validation === 0 ? "Aujourd'hui" : `${d.days_since_validation} j`}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            <Truck className="w-3 h-3" /> {d.child_bls_count}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs font-mono">
                              {d.delivered.toLocaleString('fr-FR')}/{d.total_quantity.toLocaleString('fr-FR')}
                              <span className="text-slate-400 ml-2">({d.percent}%)</span>
                            </span>
                            <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${d.percent >= 75 ? 'bg-green-500' : d.percent >= 40 ? 'bg-blue-500' : 'bg-orange-500'}`}
                                style={{ width: `${d.percent}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <a
                            href="/sales"
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-[#1B3268] text-white rounded hover:bg-[#152a55] transition-colors"
                            data-testid={`goto-sales-${d.reference}`}
                          >
                            Ouvrir
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              🎉 Aucune livraison en attente — tous les devis validés ont été entièrement livrés.
            </p>
          )}
        </div>
      </Card>

      {/* Recent Productions */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="recent-productions-card">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268]">Productions Récentes</h3>
        </div>
        <div className="p-6">
          {stats?.recent_productions?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                    <th className="p-4 text-left">Date</th>
                    <th className="p-4 text-left">Type Agglo</th>
                    <th className="p-4 text-right">Quantité</th>
                    <th className="p-4 text-right">Coût Total</th>
                    <th className="p-4 text-right">Coût Unitaire</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_productions.map((prod) => (
                    <tr key={prod.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`production-${prod.id}`}>
                      <td className="p-4">{new Date(prod.created_at).toLocaleDateString('fr-FR')}</td>
                      <td className="p-4">{prod.agglo_type}</td>
                      <td className="p-4 text-right font-mono">{prod.agglo_produced}</td>
                      <td className="p-4 text-right font-mono">{prod.total_cost.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} FCFA</td>
                      <td className="p-4 text-right font-mono">{prod.unit_cost.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} FCFA</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Aucune production récente</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
